import type { ModuleFederationRuntimePlugin } from '@module-federation/runtime'

/**
 * Cache busting for the membership preview remote
 * (docs/MODULE_FEDERATION_GUIDE.md → "Cache busting").
 *
 * The GCS buckets serving the remote attach
 * `Cache-Control: max-age=31536000, immutable` to EVERY object — including
 * `mf-manifest.json` and the fixed-name `remoteEntry.js`. Returning browsers
 * (and Google's shared edge cache) therefore keep resolving a previous remote
 * build after each remote deploy; the previous build's content-hashed chunks
 * are gone, so every editor mount fails with
 * `[MF] Failed to mount membershipPreviewApp/...`.
 *
 * Appending a per-app-boot query param to the manifest URL and to the remote
 * entry URL guarantees both are fetched fresh once per session, no matter
 * what cache headers the bucket serves. Content-hashed chunks are not
 * touched and keep their long-lived caching.
 */
export const MF_CACHE_BUST_PARAM = 'mfv'

export const createMfCacheBustStamp = (): string => Date.now().toString(36)

export const withMfCacheBust = (url: string, stamp: string): string => {
  if (!stamp || url.includes(`${MF_CACHE_BUST_PARAM}=`)) return url
  return `${url}${url.includes('?') ? '&' : '?'}${MF_CACHE_BUST_PARAM}=${stamp}`
}

/**
 * Federation runtime plugin that rewrites the resolved remote entry URL with
 * the session cache-bust param via the `loadEntry` lifecycle hook.
 *
 * `loadEntry` fires once per remote, before `@module-federation/runtime`
 * branches on `remoteInfo.type` (`loadEntryDom`'s `esm`/`module` → dynamic
 * `import()`, `system` → `System.import()`, anything else — including this
 * remote's current `global` type — → `<script>` tag via the `createScript`
 * hook). Mutating `remoteInfo.entry` here and returning nothing lets the
 * runtime's own type-specific loader continue with the busted URL, so the
 * fix stays correct even if the remote's webpack build ever changes its
 * federation library output type. Hooking `createScript` alone would only
 * cover the `<script>`-tag path and silently stop applying if the remote
 * started emitting an ESM/SystemJS entry.
 */
export const createRemoteEntryCacheBustPlugin = (stamp: string): ModuleFederationRuntimePlugin => ({
  name: 'membership-preview-entry-cache-bust',
  loadEntry({ remoteInfo }: { remoteInfo: { entry: string } }) {
    if (remoteInfo?.entry) {
      remoteInfo.entry = withMfCacheBust(remoteInfo.entry, stamp)
    }
    return undefined
  },
})
